import type { SubmissionAnswers } from '#submissions'

import { evaluateBooleanCondition } from './evaluator'
import type { Question } from './schemas/question'
import type { Section, Subtopic } from './schemas/topic'
import type { SurveyVariables } from './surveyVariables'

export const isQuestionVisible = (
  question: Question,
  answers: SubmissionAnswers,
  vars: SurveyVariables
): boolean => {
  const conditionMet =
    question.renderIf === undefined ||
    evaluateBooleanCondition(question.renderIf, { answers, vars })

  if (!conditionMet) {
    return false
  }

  return question.type === 'owner'
    ? vars.profile.maritalStatus === 'married'
    : true
}

// False means the form for these sections renders nothing at all: SectionGuard
// returns null for a section with no visible question, so every section
// collapses and the user is left looking at an empty screen with a live Save
// button. An owner question counts as invisible for an unmarried profile, which
// is what empties the loan and charitable-goals root forms.
export const hasVisibleQuestions = (
  sections: Section[],
  answers: SubmissionAnswers,
  vars: SurveyVariables
): boolean =>
  sections.some(section =>
    section.questions.some(question =>
      isQuestionVisible(question, answers, vars)
    )
  )

// A subtopic's renderIf gates the subtopic as a whole, mirroring how a field's
// renderIf works. It is evaluated against the full submission answers (plus
// profile vars); since question ids are unique across the topic tree, it may
// reference topic-level answers or another subtopic's answers (e.g. mutually
// exclusive plans). When it evaluates falsy the subtopic is hidden everywhere
// and its answers are excluded from finRecord generation.
export const isSubtopicVisible = (
  subtopic: Subtopic,
  answers: SubmissionAnswers,
  vars: SurveyVariables
): boolean =>
  subtopic.renderIf === undefined ||
  evaluateBooleanCondition(subtopic.renderIf, { answers, vars })

export const calculateQuestionsVisibility = (
  questions: Question[],
  answers: SubmissionAnswers,
  vars: SurveyVariables
): Map<string, boolean> =>
  questions.reduce(
    (map, question) =>
      map.set(question.id, isQuestionVisible(question, answers, vars)),
    new Map()
  )
